"use client";

import { Icon, ospThemes } from "@ospweb/sdk-ui";
import React, { useEffect, useRef, useState, RefObject } from "react";
import { cn } from "utils";
import { Markdown } from "../../Markdown";
import EdgenDrawer from "../../Drawer";
import StepsComponent from "./StepsComponent";
import DeepSearchContent from "./DeepSearchContent";
import Translations from "@translate/components/Chat/DeepSearch/DeepSearch";
import { I18nUtil } from "@utils/i18n";
import { useChats } from "@hooks/useChats";
import { useStickyHeaders } from "@hooks/useStickyHeaders";

const I18n = I18nUtil.createScoped(Translations);

type IDeepSearchProps = {
  data: {
    toolCallList: ReturnType<typeof useChats>["messages"][0]["toolCallList"];
    stepList: ReturnType<typeof useChats>["messages"][0]["stepList"];
    count?: number;
    reasoning?: string;
  };
  isLast?: boolean;
  loading?: boolean;
  content?: boolean;
};

export const DeepSearch = (props: IDeepSearchProps) => {
  const { isLast, loading, content } = props;
  const {
    toolCallList = [],
    stepList = [],
    count,
    reasoning = "",
  } = props?.data;
  const isScrollToBottom = isLast && loading && !content;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [time, setTime] = useState(0);
  const { currentStickyHeader = "" } = useStickyHeaders({
    containerRef: containerRef as RefObject<HTMLElement>,
    headerSelector: ".data-sticky-header",
    offset: 0,
  });

  const onStepClick = (id: string) => {
    const targetElement = document.querySelector(`#${id}`);

    if (!id || !containerRef.current || !targetElement) {
      return;
    }
    const targetRect = targetElement?.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const top =
      targetRect?.top - containerRect.top + containerRef.current.scrollTop;

    containerRef.current.scrollTo({
      top: top ?? 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const initTime = async () => {
      const storeTime = await localStorage.getItem("deepSearchTime");
      if (storeTime) {
        setTime(Number(storeTime));
      }
    };
    initTime();
    // deepSearch 滚动到最底部
    if (!isScrollToBottom || !containerRef.current) {
      localStorage.setItem("deepSearchTime", time.toString());
      timerRef.current && clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setTime((prev) => prev + 1);
    }, 1000);
    const top =
      containerRef.current.scrollHeight - containerRef.current.clientHeight;
    containerRef.current.scrollTo({
      top,
      behavior: "smooth",
    });
    return () => {
      timerRef.current && clearInterval(timerRef.current);
      localStorage.removeItem("deepSearchTime");
    };
  }, [isScrollToBottom, toolCallList?.length]);

  return (
    <div className={cn("flex flex-col gap-2")}>
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-1">
          <div className="w-[24px] h-[24px] flex items-center justify-center bg-content_brand_primary/20 rounded-[4px]">
            <Icon
              name="edgen-deepseach"
              size={12}
              color={ospThemes.colors.content_brand_primary}
            />
          </div>
          <span className="text-content_primary body_medium_l">
            {I18n.t("DeepSearch")}
          </span>
          <span className="text-content_secondary body_s ml-1">
            {isLast && time ? `${time}s${!!count ? " · " : ""}` : ""}
            {count ? `${count} sources` : ""}
          </span>
        </div>
        <Icon
          data-openreplay-label="chatbox_deepsearch_menu"
          name="menu"
          size={20}
          color={ospThemes.colors.content_tertiary}
          className="cursor-pointer"
          onClick={() => setIsOpen(true)}
        />
      </div>
      <div className="w-full h-[480px] border-2 border-border_solid flex rounded-xl overflow-hidden">
        <div className="w-0 hidden md:block md:w-[192px] h-full bg-background_white_overlay5">
          <StepsComponent
            steps={stepList}
            onStepClick={onStepClick}
            activeHeader={currentStickyHeader || ""}
          />
        </div>
        <div className="flex-1 h-full relative">
          <div className="bg-[linear-gradient(180deg,#161616_6.33%,rgba(22,22,22,0.00)_89.17%)] h-10 w-full absolute top-0 left-0"></div>
          <div
            className="flex-1 h-full overflow-y-auto no-scrollbar px-6 pb-6"
            ref={containerRef}
          >
            <DeepSearchContent data={{ toolCallList, stepList }} />
          </div>
          <div className="bg-[linear-gradient(0deg,#161616_6.33%,rgba(22,22,22,0.00)_89.17%)] h-10 w-full absolute bottom-0 left-0"></div>
        </div>
      </div>
      <EdgenDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="DeepThink"
      >
        <div className="text-content_primary body_m">
          {reasoning && <Markdown>{reasoning}</Markdown>}
        </div>
      </EdgenDrawer>
    </div>
  );
};
